import {
  isMgmtTypeFilterApplied,
  MgmtFilAppliedFilterRenderer,
  MgmtTypeFilterBody,
  mgmtTypeFilterDataExtractor,
} from 'app/features/teams/messages/table-filters/MgmtTypeActionsFilter'
import {
  isPodsApplied,
  podFilterDataExtractor,
  PodsAppliedFilterRenderer,
  PodsFilterBody,
} from 'app/features/teams/messages/table-filters/PodsFilter'
import { standardUTCFormat } from 'app/sm/helpers'
import {
  ApplicationWorkflowAppliedFilterRenderer,
  ApplicationWorkflowFilterBody,
  applicationWorkflowFilterDataExtractor,
  isApplicationWorkflowApplied,
} from 'app/sm/manager_viewings/ManagerViewingsFilters/ApplicationWorkflowFilter'
import {
  isPropertyManagerApplied,
  PropertyManagerAppliedFilterRenderer,
  PropertyManagerFilterBody,
  propertyManagerFilterDataExtractor,
} from 'app/sm/manager_viewings/ManagerViewingsFilters/PropertyManagerFilter'
import {
  SortAppliedFilterRenderer,
  sortDataExtractor,
  SortFilterBody,
} from 'app/sm/manager_viewings/ManagerViewingsFilters/SortFilter'

export const filtersIds = {
  pods: 'pods',
  manager: 'manager',
  workflow: 'workflow',
  sort: 'sort',
  date: 'datePickerValue',
  mgmtType: 'mgmtType',
}

export const filtersInitialState = {
  [filtersIds.pods]: {},
  [filtersIds.manager]: {},
  [filtersIds.workflow]: {},
  [filtersIds.sort]: {},
  [filtersIds.date]: null,
  [filtersIds.mgmtType]: null,
}

export const filtersAppliedChecker = {
  [filtersIds.manager]: isPropertyManagerApplied,
  [filtersIds.pods]: isPodsApplied,
  [filtersIds.workflow]: isApplicationWorkflowApplied,
  [filtersIds.mgmtType]: isMgmtTypeFilterApplied,
}

export const PodsFilterWarning =
  'Managers already filtered, applying pods filter will discard managers filter.'
export const ManagersFilterWarning =
  'Pods already filtered, applying managers filter will discard pods filter.'

export const hasAppliedFilters = (
  filtersState: typeof filtersInitialState,
  enableFilters: string[],
) => {
  if (!filtersState) return false
  return Object.values(filtersIds)
    .filter((filterId) => enableFilters.includes(filterId))
    .some((filterId) => {
      const filterValue = filtersState[filterId]
      const filterAppliedChecker = filtersAppliedChecker[filterId]
      return filterAppliedChecker?.(filterValue)
    })
}

export const getFilters = (filtersState: typeof filtersInitialState) => {
  const managers = propertyManagerFilterDataExtractor(filtersState).managers
  const pods = podFilterDataExtractor(filtersState).pods
  const workflow = applicationWorkflowFilterDataExtractor(filtersState).workflow
  const sort = sortDataExtractor(filtersState).sort
  const mgmtType = mgmtTypeFilterDataExtractor(filtersState)

  return {
    managers,
    pods,
    workflow,
    sort,
    mgmtType: mgmtType.mgmtType,
    listingStatus: mgmtType.listingStatus,
  }
}

const getRenderedFiltersConfig = (
  filterId: any,
  filtersValues: any,
  updateFilterFn: any,
) => {
  const filterValues = filtersValues[filterId]
  return {
    filterValues,
    isApplied: filtersAppliedChecker[filterId]?.(filterValues),
    setFilterValues: (values: any) => updateFilterFn(filterId, values),
  }
}

export const buildRenderedFilterConfig = (
  filtersValues: any,
  updateFilterFn?: any,
  { includeListingStatusFilter = false } = {},
) => {
  const propertyFilterLabel = includeListingStatusFilter
    ? 'Property'
    : 'Management Type'
  return {
    [filtersIds.manager]: {
      AppliedFilterRenderer: PropertyManagerAppliedFilterRenderer,
      FilterBody: PropertyManagerFilterBody,
      filterTriggerPlaceholder: 'User',
      appliedFilterTitle: 'User',
      ...getRenderedFiltersConfig(
        filtersIds.manager,
        filtersValues,
        updateFilterFn,
      ),
    },
    [filtersIds.pods]: {
      AppliedFilterRenderer: PodsAppliedFilterRenderer,
      FilterBody: PodsFilterBody,
      filterTriggerPlaceholder: 'Pod',
      appliedFilterTitle: 'Pod',
      ...getRenderedFiltersConfig(
        filtersIds.pods,
        filtersValues,
        updateFilterFn,
      ),
    },
    [filtersIds.mgmtType]: {
      AppliedFilterRenderer: MgmtFilAppliedFilterRenderer,
      FilterBody: MgmtTypeFilterBody,
      filterTriggerPlaceholder: propertyFilterLabel,
      appliedFilterTitle: propertyFilterLabel,
      ...getRenderedFiltersConfig(
        filtersIds.mgmtType,
        filtersValues,
        updateFilterFn,
      ),
    },

    [filtersIds.workflow]: {
      AppliedFilterRenderer: ApplicationWorkflowAppliedFilterRenderer,
      FilterBody: ApplicationWorkflowFilterBody,
      filterTriggerPlaceholder: 'Status',
      appliedFilterTitle: 'Status',
      ...getRenderedFiltersConfig(
        filtersIds.workflow,
        filtersValues,
        updateFilterFn,
      ),
    },
    [filtersIds.sort]: {
      AppliedFilterRenderer: SortAppliedFilterRenderer,
      FilterBody: SortFilterBody,
      filterTriggerPlaceholder: 'Sort',
      appliedFilterTitle: 'Sort',
      ...getRenderedFiltersConfig(
        filtersIds.sort,
        filtersValues,
        updateFilterFn,
      ),
    },
    [filtersIds.date]: {
      filterId: filtersIds.date,
      format: standardUTCFormat,
      filterTriggerPlaceholder: 'Date',
      appliedFilterTitle: 'Date',
      ...getRenderedFiltersConfig(
        filtersIds.date,
        filtersValues,
        updateFilterFn,
      ),
    },
  }
}
